import { fromBaseRecord, type FromBaseRow as Row } from './from-base-context';
import { encounterCreatureField, layerOf, sizeIdOf } from '../../shared/array-types';
import { readItemField } from './item-field-reader';
import { collectLightAndDarkSources } from '../../encounter-map/ft3d/perception';
import { readInventoryField } from './inventory-raw-readers';
import { projectInventoryStatusLight } from './inventory-status-light';

export const LIGHT_CONDITION_LAYERS = ['if_light_type_active', 'if_light_type_not_active',
    'if_effective_light_type_active', 'if_effective_light_type_not_active', 'if_outdoors_active',
    'if_outdoors_not_active', 'if_sunlight_active', 'if_sunlight_not_active'] as const;
export const SCENE_CONDITION_LAYERS = ['if_caster_summon_within_ft', ...LIGHT_CONDITION_LAYERS] as const;

const upper = (value: unknown) => String(value ?? '').toUpperCase();
export const conditionMemberId = (row: Row) => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
const rows = (value: unknown): Row[] => Array.isArray(value) ? value.filter(fromBaseRecord) : [];

// Saved writers replace raw rows and catalog dictionaries. Keep answers tied to
// both identities: a new catalog also invalidates unchanged members' answers.
const definitionAnswers = new WeakMap<Row, Map<string, number>>();
const memberAnswers = new WeakMap<Row, { catalog: Row; answer: number }>();

function readConditionKinds(pending: unknown[], reference: (id: string) => number): number {
    let answer = 0;
    while (pending.length) {
        const value = pending.pop();
        if (typeof value === 'string') {
            answer |= reference(upper(value));
        } else if (value && typeof value === 'object') {
            if (fromBaseRecord(value)) {
                if (readItemField(value, 'if_caster_summon_within_ft') != null) answer |= 1;
                if (LIGHT_CONDITION_LAYERS.some(key => readItemField(value, key) != null)) answer |= 2;
                if (['bright_light_dim_light', 'magical_darkness', 'sunlight_light'].some(key => readItemField(value, key) != null)) answer |= 4;
            }
            pending.push(...Object.values(value));
        }
    }
    return answer;
}

function definitionConditionKinds(id: string, catalog: Row, answers: Map<string, number>): number {
    if (!fromBaseRecord(catalog[id])) return 0;
    const known = answers.get(id);
    if (known !== undefined) return known;
    const seen = new Set([id]), pending: unknown[] = [catalog[id]];
    const answer = readConditionKinds(pending, next => {
        const known = answers.get(next);
        if (known !== undefined) return known;
        if (!seen.has(next) && fromBaseRecord(catalog[next])) { seen.add(next); pending.push(catalog[next]); }
        return 0;
    });
    // Only a fully negative closure proves every visited definition negative.
    // On a positive cycle, siblings may not reach the branch that supplied it.
    if (!answer) for (const next of seen) answers.set(next, 0);
    answers.set(id, answer);
    return answer;
}

/** Follow only this member's saved references into its held definition closure.
 * Catalog siblings and other members are not condition dependencies. */
function memberConditionKinds(member: Row, catalog: Row): number {
    const known = memberAnswers.get(member);
    if (known?.catalog === catalog) return known.answer;
    let definitions = definitionAnswers.get(catalog);
    if (!definitions) definitionAnswers.set(catalog, definitions = new Map());
    const pending: unknown[] = Object.entries(member)
        .filter(([key]) => !['catalog', 'encounter_context', 'caster_summons', 'status_owners'].includes(key)).map(([, value]) => value);
    const answer = readConditionKinds(pending, id => definitionConditionKinds(id, catalog, definitions));
    memberAnswers.set(member, { catalog, answer });
    return answer;
}

export const memberReadsSummonRange = (member: Row, catalog: Row): boolean => (memberConditionKinds(member, catalog) & 1) !== 0;
export const memberReadsLightContext = (member: Row, catalog: Row): boolean => (memberConditionKinds(member, catalog) & 2) !== 0;
export const memberReadsSceneConditions = (member: Row, catalog: Row): boolean => (memberConditionKinds(member, catalog) & 3) !== 0;

/** Only the condition's geometry and eligibility facts cross the member/worker
 * boundary. Omitted zero EC fields keep their ordinary reader defaults. */
export function summonConditionFact(member: Row): Row {
    const keys = ['encounter_creature_id', 'encounter_id', 'controller_encounter_creature_id',
        'encounter_creature_dead', 'encounter_creature_deleted', 'encounter_creature_private'];
    const ec = Object.fromEntries(keys.map(key => [key, encounterCreatureField(member.encounter_creatures, key) ?? null]));
    const position = fromBaseRecord(member.position) ? member.position : fromBaseRecord(member.encounter_creatures) ? member.encounter_creatures : {};
    return { encounter_creatures: ec,
        position: Object.fromEntries(['x_pos', 'y_pos', 'z_pos'].map(key => [key, encounterCreatureField(position, key) ?? 0])),
        item_layer_types: { size: sizeIdOf(member) ?? null, swarm_width_lanes: layerOf(member, 'swarm_width_lanes') ?? null } };
}

/** An enclosing encounter requires its roster, even when the roster is lost.
 * Standalone creature compilation supplies its separate sheet scope. */
export function memberConditionContext(base: Row, member: Row, resolve?: (member: Row) => Row): unknown {
    const context: Row = { ...(fromBaseRecord(member.encounter_context) ? member.encounter_context : {}),
        ...(base.encounters === undefined ? {} : { encounters: base.encounters }),
        ...(base.campaign_arr === undefined ? {} : { campaign_arr: base.campaign_arr }), summon_range_scope: 'encounter' };
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    if (memberReadsLightContext(member, catalog) && Array.isArray(base.encounter_creatures_arr) && Array.isArray(base.inventory)) {
        const sources = rows(base.condition_roster ?? base.encounter_creatures_arr)
            .filter(row => Number(encounterCreatureField(row.encounter_creatures, 'encounter_creature_deleted') ?? 0) !== 1);
        // Complete only the light/status family for readers, including this host:
        // resolving a whole condition-reading member here would recurse. Other
        // emitters may borrow their ordinary initial compile. Saved-row deltas
        // already supply completed rows and never run either producer.
        const lightingRoster = base.condition_roster || !resolve ? sources : sources.map(source => {
            if ((memberConditionKinds(source, catalog) & 4) === 0) return source;
            if (source !== member && !memberReadsSceneConditions(source, catalog)) return resolve(source);
            return projectInventoryStatusLight({ ...source, statuses: { target: [], caster: [] } },
                { ...source, catalog, position: summonConditionFact(source).position },
                id => { const definition = catalog[upper(id)]; return fromBaseRecord(definition) ? definition : null; });
        });
        // Borrow held rows only long enough for the shared collector. The member
        // and worker carry source facts, never another copy of the encounter.
        context.condition_light_sources = collectLightAndDarkSources({ encounters: base.encounters, catalog,
            statuses: base.statuses, encounter_creatures_arr: lightingRoster,
            encounter_inventories_arr: rows(base.condition_inventory ?? base.inventory)
                .filter(row => Number(readInventoryField(row, 'inventory_deleted') ?? 0) !== 1) });
    }
    if (!memberReadsSummonRange(member, catalog)) return context;
    const roster = rows(base.encounter_creatures_arr), id = conditionMemberId(member);
    if (!roster.some(row => conditionMemberId(row) === id)) return context;
    const held = rows(base.condition_roster);
    const summons = roster.filter(row => upper(encounterCreatureField(row.encounter_creatures, 'controller_encounter_creature_id')) === id)
        .filter(row => !['encounter_creature_dead', 'encounter_creature_deleted', 'encounter_creature_private']
            .some(key => Number(encounterCreatureField(row.encounter_creatures, key) ?? 0) === 1))
        .map(row => summonConditionFact(held.find(held => conditionMemberId(held) === conditionMemberId(row)) ?? resolve?.(row) ?? row));
    return { ...context, controlled_summons: summons };
}
